import useFormError from 'hooks/useFormErrors';
import {FC, useMemo} from 'react';
import {Controller, useFormContext} from 'react-hook-form';
import {c} from 'ttag';

import Field from '../Field';
import {sanitizeNatural} from '../InputSanitized/utils';
import {FormContextString} from '../types';
import {getRegisterRules} from '../utils';
import InputIncrementalBase, {
    InputIncrementalBaseProps,
    useIncrementalSanitizer,
} from './InputIncrementalBase';

export type InputIncrementalProps = {
    name: string;
    step?: number;
} & Omit<InputIncrementalBaseProps, 'onDecrement' | 'onIncrement'>;

const InputIncremental: FC<InputIncrementalProps> = ({
    className,
    defaultValue,
    disabled,
    error,
    helpText,
    label,
    max = 10,
    min = 1,
    name,
    required,
    requiredType,
    rules,
    step = 1,
    ...rest
}) => {
    const {control, getValues, register, watch} =
        useFormContext<FormContextString>();

    const registerRules = getRegisterRules({disabled, required, rules});

    const {...registration} = register(name, registerRules);

    const sanitizer = useIncrementalSanitizer(min, max);

    const value = watch(name);
    const errorValue = useMemo(() => {
        if (Number(value) > max) {
            return {
                message: c('component')
                    .t`Ensure this value is less than or equal to ${max}.`,
                type: 'max',
            };
        }

        if (Number(value) < min) {
            return {
                message: c('component')
                    .t`Ensure this value is greater than or equal to ${min}.`,
                type: 'min',
            };
        }

        return undefined;
    }, [max, min, value]);

    const fieldError = useFormError(name, error || errorValue);

    return (
        <Field
            className={className}
            disabled={registerRules?.disabled}
            error={fieldError}
            helpText={helpText}
            label={label}
            name={name}
            required={!!registerRules?.required}
            requiredType={requiredType}
            type="input"
        >
            <Controller
                control={control}
                defaultValue={defaultValue ? defaultValue.toString() : ''}
                render={({field: {onChange, ...fieldRest}}) => (
                    <InputIncrementalBase
                        aria-label={
                            rest['aria-label'] || label === null
                                ? undefined
                                : typeof label === 'string'
                                  ? label
                                  : name
                        }
                        disabled={registerRules?.disabled}
                        error={fieldError}
                        onChange={({target}) => {
                            const sanitized = sanitizeNatural(target.value);
                            onChange(
                                sanitized === ''
                                    ? ''
                                    : String(Number(sanitized)),
                            );
                        }}
                        onDecrement={() => {
                            onChange(
                                sanitizer(
                                    String(
                                        Number(
                                            getValues(name) || defaultValue,
                                        ) - step,
                                    ),
                                ),
                            );
                        }}
                        onIncrement={() => {
                            onChange(
                                sanitizer(
                                    String(
                                        Number(
                                            getValues(name) || defaultValue,
                                        ) + step,
                                    ),
                                ),
                            );
                        }}
                        {...rest}
                        {...fieldRest}
                    />
                )}
                {...registration}
            />
        </Field>
    );
};

export default InputIncremental;
